<template>
  <div class="expression-tree-view">
    <div class="expression-tree-view__grid" ref="gridElement" :style="{
      'grid-template-columns': `repeat(${columnCount}, max-content)`,
      'grid-template-rows': `repeat(${rowCount}, 1rem)`,
    }">
      <expression-tree-node
        v-for="node in allTreeNodes.values()"
        :key="node.id"
        :tree="expressionTree"
        :node="node"
        :editable="editable"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toValue, provide, ref, watch } from "vue";
import { onMounted, onBeforeUnmount } from "vue";
import { wrapGrid } from "@core/composables/useAnimatedGrid";

import ExpressionTreeNode from "./ExpressionTreeNode.vue";

import { useExpressionTree, type ExpressionTree } from "./ExpressionTree";
import type { InputNode } from "./ExpressionInputTypes";
import type { ExpressionParseContext } from "../effect/Expression.ts";
import { useTargetStore } from "../target/targets.store.ts";
import { useSettingsStore } from "@editor/store/settings.store";
import { useResourceStore } from "../resource/resources.store.ts";
import { useIndexStore } from "../index/indices.store.ts";

const props = withDefaults(defineProps<{
  expression?: string;
  parseContext?: ExpressionParseContext;
  tree?: ExpressionTree;
  editable?: boolean;
}>(), {
  editable: false
});

// TREE

const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const settings = useSettingsStore();

const expressionParseContext = computed(
  (): ExpressionParseContext => ({
    roundIndex: 0,
    targets: targetStore.items,
    indices: indexStore.items,
    resources: resourceStore.items,
    simulationSettings: {
      roundUnit: settings.simulationRoundUnit,
      roundLength: settings.simulationRoundLength,
      roundAmount: settings.simulationRoundAmount,
      timeStart: settings.simulationTimeStart,
      durationType: settings.simulationDurationType
    },
    ...props.parseContext
  })
);

const expressionTree = ref(
  props.tree ?? useExpressionTree(expressionParseContext, props.expression)
);
const lastExpression = ref(props.expression);

// Listen for changes in the expression prop
watch(() => props.expression, (value) => {
  if (value == null) return;
  if (value == lastExpression.value) return;
  lastExpression.value = value;
  expressionTree.value.setExpression(value);
});


// POSITIONING

type NodePosition = {
  id: string;
  x: number;
  y: number;
  type: InputNode["type"];
  name: string;
  leftChild?: string;
  rightChild?: string;
  topChild?: string;
};
type NodePositionMap = Map<string, NodePosition>;

const gridElement = ref<HTMLElement | null>(null);

onMounted(() => {
  // animate the grid
  if (gridElement.value) {
    const { unwrapGrid } = wrapGrid(gridElement.value, { duration: 400 });
    onBeforeUnmount(() => {
      if (gridElement.value) {
        unwrapGrid();
      }
    });
  }
});

const allTreeNodes = computed(() => {
  const nodes = new Map<string, InputNode>();
  addNodeAndChildren(expressionTree.value.state.root, nodes);
  return nodes;
});

function addNodeAndChildren(node: InputNode, nodes: Map<string, InputNode>) {
  nodes.set(node.id, node);
  if ("args" in node) {
    for (const arg of node.args) {
      addNodeAndChildren(arg, nodes);
    }
  }
}

const compactedPositions = computed(() => {
  const positions = getVirtualPositions();
  compactizePositionTree(positions);
  // make all positions take two spaces
  multiplyAllPositions(positions, 2);

  moveToPositiveRange(positions);
  console.log("positions", positions);
  return positions;
});

const columnCount = computed(() => {
  let max = 0;
  for (const pos of compactedPositions.value) {
    if (pos[1].x > max) {
      max = pos[1].x;
    }
  }
  return max;
});
const rowCount = computed(() => {
  let max = 0;
  for (const pos of compactedPositions.value) {
    if (pos[1].y > max) {
      max = pos[1].y;
    }
  }
  return max;
});

provide("treeNodePositions", compactedPositions);

function multiplyAllPositions(positions: NodePositionMap, factor: number) {
  for (const pos of positions) {
    pos[1].x *= factor;
    pos[1].y *= factor;
  }
}

/**
 * Move all nodes closer to each other by moving groups of nodes to the left.
 * This should make the tree more readable.
 */
function compactizePositionTree(positions: NodePositionMap) {
  const nodeIds = Array.from(positions.keys());

  for (const nodeId of nodeIds) {
    const node = positions.get(nodeId)!;
    if (node.type == "operator" || (node.type == "function" && node.topChild == null)) {
      const positionLines = positionsAsLines(positions);

      // move the right branch to the left
      const rightBranchNodes = getBranchNodes(node.rightChild!, positions);
      const rightDistanceToOperator = rightBranchNodes[0].x - node.x;
      const leftSpace = Math.min(getFreeLeftSpace(rightBranchNodes, positions, positionLines), rightDistanceToOperator);
      if (leftSpace > 1) {
        moveBranch(rightBranchNodes, leftSpace - 1);
      }

      // move the left branch to the right
      const leftBranchNodes = getBranchNodes(node.leftChild!, positions);
      const leftDistanceToOperator = node.x - leftBranchNodes[0].x;
      const rightSpace = Math.min(getFreeRightSpace(leftBranchNodes, positions, positionLines), leftDistanceToOperator);
      if (rightSpace > 1) {
        moveBranch(leftBranchNodes, -(rightSpace - 1));
      }

      // TODO: center tree by moving all subnodes
    }
  }
}

/**
 * Move all nodes to the right so that the leftmost node is at x=1
 * and down so that the topmost node is at y=1.
 */
function moveToPositiveRange(positions: NodePositionMap) {
  const nodeIds = Array.from(positions.keys());

  // move all nodes to the right
  let minX = Infinity;
  for (const nodeId of nodeIds) {
    const node = positions.get(nodeId)!;
    if (node.x < minX) {
      minX = node.x;
    }
  }
  for (const nodeId of nodeIds) {
    const node = positions.get(nodeId)!;
    node.x -= minX - 1;
  }

  // move all nodes down
  let minY = Infinity;
  for (const nodeId of nodeIds) {
    const node = positions.get(nodeId)!;
    if (node.y < minY) {
      minY = node.y;
    }
  }
  for (const nodeId of nodeIds) {
    const node = positions.get(nodeId)!;
    node.y -= minY - 1;
  }
}

function getBranchNodes(nodeId: string, positions: NodePositionMap): NodePosition[] {
  const node = positions.get(nodeId)!;
  if (node.type == "operator") {
    const leftBranchNodes = getBranchNodes(node.leftChild!, positions);
    const rightBranchNodes = getBranchNodes(node.rightChild!, positions);
    return [node, ...leftBranchNodes, ...rightBranchNodes];
  } else if (node.type == "function") {
    const nodes = [node];
    if (node.leftChild) {
      const leftBranchNodes = getBranchNodes(node.leftChild, positions);
      nodes.push(...leftBranchNodes);
    }
    if (node.topChild) {
      const topBranchNodes = getBranchNodes(node.topChild, positions);
      nodes.push(...topBranchNodes);
    }
    if (node.rightChild) {
      const rightBranchNodes = getBranchNodes(node.rightChild, positions);
      nodes.push(...rightBranchNodes);
    }
    return nodes;
  } else if (node.type == "parenthesis") {
    return getBranchNodes(node.topChild!, positions);
  } else {
    return [node];
  }
}

/**
 * Check how far all the nodes of the branch can be moved to the left
 * wihout overlapping with other nodes.
 */
function getFreeLeftSpace(nodeBranch: NodePosition[], positions: NodePositionMap, positionLines: Map<number, NodePosition[]>) {
  let freeSpace = Infinity;
  const nodeIds = nodeBranch.map(n => n.id);
  for (const node of nodeBranch) {
    const distanceLeft = getDistanceLeft(node.id, positions, positionLines, nodeIds);
    if (distanceLeft < freeSpace) {
      freeSpace = distanceLeft;
    }
  }
  return freeSpace;
}

function getDistanceLeft(nodeId: string, positions: NodePositionMap, positionLines: Map<number, NodePosition[]>, nodeIdsToIgnore: string[]) {
  const node = positions.get(nodeId)!;
  const line = positionLines.get(node.y)!;
  const index = line.findIndex(n => n.id == nodeId);
  if (index == 0) {
    return Infinity;
  }
  const leftNeighbour = line[index - 1];
  if (nodeIdsToIgnore.includes(leftNeighbour.id)) {
    return getDistanceLeft(leftNeighbour.id, positions, positionLines, nodeIdsToIgnore);
  }
  const distance = node.x - leftNeighbour.x - 1;
  return distance;
}

function getFreeRightSpace(nodeBranch: NodePosition[], positions: NodePositionMap, positionLines: Map<number, NodePosition[]>) {
  let freeSpace = Infinity;
  const nodeIds = nodeBranch.map(n => n.id);
  for (const node of nodeBranch) {
    const distanceRight = getDistanceRight(node.id, positions, positionLines, nodeIds);
    if (distanceRight < freeSpace) {
      freeSpace = distanceRight;
    }
  }
  return freeSpace;
}

function getDistanceRight(nodeId: string, positions: NodePositionMap, positionLines: Map<number, NodePosition[]>, nodeIdsToIgnore: string[]) {
  const node = positions.get(nodeId)!;
  const line = positionLines.get(node.y)!;
  const index = line.findIndex(n => n.id == nodeId);
  if (index == line.length - 1) {
    return Infinity;
  }
  const rightNeighbour = line[index + 1];
  if (nodeIdsToIgnore.includes(rightNeighbour.id)) {
    return getDistanceRight(rightNeighbour.id, positions, positionLines, nodeIdsToIgnore);
  }
  const distance = rightNeighbour.x - node.x - 1;
  return distance;
}

function moveBranch(branchNodes: NodePosition[], distance: number) {
  for (const node of branchNodes) {
    node.x -= distance;
  }
}

function positionsAsLines(positions: NodePositionMap) {
  const lines = new Map<number, NodePosition[]>();
  for (const pos of positions) {
    if (pos[1].type == "parenthesis") {
      continue;
    }
    const line = pos[1].y;
    if (!lines.has(line)) {
      lines.set(line, []);
    }
    lines.get(line)!.push(pos[1]);
  }
  for (const line of lines) {
    line[1].sort((a, b) => a.x - b.x);
  }
  return lines;
}

function getVirtualPositions() {
  const positions = new Map<string, NodePosition>();
  updateVirtualPosition(positions, expressionTree.value.state.root, { x: 0, y: 0 });
  return positions;
}

function updateVirtualPosition(
  positionMap: NodePositionMap,
  node: InputNode,
  basePos: { x: number; y: number }
): { width: number; left: number; right: number } {
  const label = toValue(expressionTree.value.getNodeLabel(node));

  if (
    node.type == "constant" ||
    node.type == "variable" ||
    node.type == "item"
  ) {
    positionMap.set(node.id, { id: node.id, x: basePos.x, y: basePos.y, name: label, type: node.type });
    return { width: 1, left: 0, right: 0 };
  }

  if (node.type == "parenthesis") {
    const spaces = updateVirtualPosition(positionMap, node.args[0], basePos);
    // positionMap.set(node.id, { id: node.id, x: basePos.x, y: basePos.y, name: "()", type: node.type, topChild: node.args[0].id });
    return spaces;
  }

  if (node.type == "operator") {
    const pos = { ...basePos };

    let x = pos.x;
    let y = pos.y - 1;

    const leftBranch = updateVirtualPosition(positionMap, node.args[0], {
      x,
      y
    });

    x += leftBranch.width;

    positionMap.set(node.id, {
      id: node.id,
      x: x,
      y: basePos.y,
      name: label,
      type: node.type,
      leftChild: unwrapParenthesis(node.args[0]).id,
      rightChild: unwrapParenthesis(node.args[1]).id
    });

    x += 1;

    const rightBranch = updateVirtualPosition(positionMap, node.args[1], {
      x,
      y
    });

    return {
      width: leftBranch.width + 1 + rightBranch.width,
      left: leftBranch.width,
      right: rightBranch.width
    };
  }

  if (node.type == "function") {
    if (node.args.length == 1) {
      let x = basePos.x;

      const topBranch = updateVirtualPosition(positionMap, node.args[0], {
        x,
        y: basePos.y - 2
      });

      x += topBranch.left;

      const pos: NodePosition = {
        id: node.id,
        x,
        y: basePos.y,
        name: label,
        type: node.type,
        topChild: unwrapParenthesis(node.args[0]).id
      };

      positionMap.set(node.id, pos);

      return {
        width: topBranch.width,
        left: topBranch.left,
        right: topBranch.right
      };
    } else if (node.args.length == 2) {
      let x = basePos.x;
      const y = basePos.y - 1;

      const leftBranch = updateVirtualPosition(positionMap, node.args[0], {
        x,
        y
      });

      x += leftBranch.width;

      positionMap.set(node.id, {
        id: node.id,
        x: x,
        y: basePos.y,
        name: label,
        type: node.type,
        leftChild: unwrapParenthesis(node.args[0]).id,
        rightChild: unwrapParenthesis(node.args[1]).id
      });

      x += 1;

      const rightBranch = updateVirtualPosition(positionMap, node.args[1], {
        x,
        y
      });

      return {
        width: leftBranch.width + 1 + rightBranch.width,
        left: leftBranch.width,
        right: rightBranch.width
      };
    } else if (node.args.length == 3) {
      let x = basePos.x;
      const y = basePos.y - 1;

      const leftBranch = updateVirtualPosition(positionMap, node.args[1], {
        x,
        y
      });

      x += leftBranch.width;

      const topBranch = updateVirtualPosition(positionMap, node.args[0], {
        x,
        y: y - 3
      });

      x += topBranch.left;

      positionMap.set(node.id, {
        id: node.id,
        x,
        y: basePos.y,
        name: label,
        type: node.type,
        leftChild: unwrapParenthesis(node.args[1]).id,
        topChild: unwrapParenthesis(node.args[0]).id,
        rightChild: unwrapParenthesis(node.args[2]).id
      });

      x += 1 + topBranch.right;

      const rightBranch = updateVirtualPosition(positionMap, node.args[2], {
        x,
        y
      });

      return {
        width: leftBranch.width + topBranch.width + rightBranch.width,
        left: leftBranch.width,
        right: rightBranch.width
      };
    }

    console.error("Invalid function argument count");
    return { width: 0, left: 0, right: 0 };
  }

  console.error("Invalid node type", node);
  return { width: 0, left: 0, right: 0 };
}

function unwrapParenthesis(node: InputNode): InputNode {
  if (node.type == "parenthesis") {
    return unwrapParenthesis(node.args[0]);
  }
  return node;
}
</script>

<style lang="scss">
.expression-tree-view {
  overflow: auto;
  min-height: 10rem;
  height: auto;
  resize: vertical;
  position: relative;
  border: 1px solid var(--color-border);
  border-radius: 5px;
  background-color: var(--color-background);
  
  --node-min-height: 4rem;
  --operator-gap: 1rem;

  &__grid {
    width: min-content;
    padding: 2rem;
    display: grid;
    gap: 1rem;
    row-gap: 2rem;
    transition: 400ms ease-out;
  }
}
</style>
